import {
  AfterViewInit,
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  input,
  signal,
  viewChildren,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ControlValueAccessor, FormsModule, NgControl } from '@angular/forms';
import { VxButtonComponent } from '../button/button.component';
import {
  PRISTINE_CONTROL,
  readControl,
  VxControlSnapshot,
} from '../form-field/form-control-state';
import {
  VX_FORM_CONTROL,
  VxFormControl,
} from '../form-field/form-field.tokens';
import { VxIconComponent } from '../icon/icon.component';
import { VxIconButtonComponent } from '../icon-button/icon-button.component';
import { VxInputComponent } from '../input/input.component';
import { VxInputSize } from '../input/input.variants';
import { VxKeyValuePair } from './kv-editor.types';

let nextId = 0;

/**
 * Editable list of key/value rows — request headers, labels, query params:
 *
 * ```html
 * <vx-form-field label="Request headers" [errors]="headerErrors()">
 *   <vx-kv-editor
 *     formControlName="headers"
 *     keyPlaceholder="Header" valuePlaceholder="Value"
 *     addLabel="Add header" removeLabel="Remove header" />
 * </vx-form-field>
 * ```
 *
 * Implements `ControlValueAccessor` with a `VxKeyValuePair[]` value. Every
 * keystroke writes through, so the bound control always holds exactly what
 * is on screen — including rows that are still empty. Deciding what an
 * incomplete row means (drop it, reject it) is the consumer's: put a
 * validator on the control and the surrounding `<vx-form-field>` shows the
 * message, the same as for any other control.
 *
 * Provides `VX_FORM_CONTROL` itself, so a wrapping field labels the editor
 * (its label points at the first key box) rather than one of the inner
 * inputs.
 */
@Component({
  selector: 'vx-kv-editor',
  templateUrl: './kv-editor.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FormsModule,
    VxButtonComponent,
    VxIconButtonComponent,
    VxIconComponent,
    VxInputComponent,
  ],
  providers: [{ provide: VX_FORM_CONTROL, useExisting: VxKvEditorComponent }],
  host: { class: 'flex w-full flex-col gap-2 font-brand' },
})
export class VxKvEditorComponent
  implements VxFormControl, ControlValueAccessor, AfterViewInit
{
  readonly size = input<VxInputSize>('md');

  readonly keyPlaceholder = input('');

  readonly valuePlaceholder = input('');

  /** Label of the button that appends an empty row. Already translated. */
  readonly addLabel = input.required<string>();

  /** Accessible name of every row's remove button. Already translated. */
  readonly removeLabel = input.required<string>();

  /** Disables the editor independently of the form's own disabled state. */
  readonly disabled = input(false);

  private readonly ngControl = inject(NgControl, {
    self: true,
    optional: true,
  });

  private readonly destroyRef = inject(DestroyRef);

  private readonly uid = `vx-kv-editor-${nextId++}`;

  /** Key box first, value box second, per row — DOM order. */
  private readonly inputs = viewChildren(VxInputComponent);

  private readonly disabledByForm = signal(false);

  private readonly controlState = signal<VxControlSnapshot>(PRISTINE_CONTROL);

  private onChange: (value: VxKeyValuePair[]) => void = () => undefined;

  private onTouched: () => void = () => undefined;

  protected readonly rows = signal<readonly VxKeyValuePair[]>([]);

  protected readonly isDisabled = computed(
    () => this.disabled() || this.disabledByForm()
  );

  /**
   * The first row's key box, so a wrapping field's label lands on something
   * typeable. With no rows the id points nowhere, which is harmless.
   */
  readonly controlId = computed(
    () => this.inputs()[0]?.controlId() ?? `${this.uid}-empty`
  );

  readonly invalid = computed(() => {
    const state = this.controlState();
    return state.touched && state.invalid;
  });

  readonly errors = computed(() => this.controlState().errors);

  readonly required = computed(() => this.controlState().required);

  constructor() {
    this.initValueAccessor();
  }

  ngAfterViewInit(): void {
    this.initControlStateSync();
  }

  /**
   * Self-registration instead of an `NG_VALUE_ACCESSOR` provider — the
   * provider form would create a circular dependency on the component itself.
   */
  private initValueAccessor(): void {
    if (this.ngControl) {
      this.ngControl.valueAccessor = this;
    }
  }

  /**
   * After view init because `NgControl.control` is only bound once the
   * directive's own `ngOnChanges` has run.
   */
  private initControlStateSync(): void {
    const control = this.ngControl?.control;
    if (!control) {
      return;
    }
    this.controlState.set(readControl(control));
    control.events
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.controlState.set(readControl(control)));
  }

  protected addRow(): void {
    this.commit([...this.rows(), { key: '', value: '' }]);
  }

  protected removeRow(index: number): void {
    this.commit(this.rows().filter((_, i) => i !== index));
    this.onTouched();
  }

  protected updateKey(index: number, key: string): void {
    this.commit(
      this.rows().map((row, i) => (i === index ? { ...row, key } : row))
    );
  }

  protected updateValue(index: number, value: string): void {
    this.commit(
      this.rows().map((row, i) => (i === index ? { ...row, value } : row))
    );
  }

  protected handleBlur(): void {
    this.onTouched();
  }

  private commit(rows: VxKeyValuePair[]): void {
    this.rows.set(rows);
    this.onChange(rows);
  }

  writeValue(value: readonly VxKeyValuePair[] | null | undefined): void {
    this.rows.set((value ?? []).map(row => ({ ...row })));
  }

  registerOnChange(fn: (value: VxKeyValuePair[]) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabledByForm.set(isDisabled);
  }
}
